import { useEffect, useState } from 'react';
import { useAuth, useLocale } from '../../context';
import { api } from '../../api';
import { getDeviceId } from '../../utils/device';
import { formatPhoneInput, normalizePhone, phonePlaceholder } from '../../utils/phone';
import { buildAuthValidationCopy, mapAuthApiError, validateLogin } from './authValidation';
import AuthSocialProviders from './AuthSocialProviders';
import WebAuthnLoginButton from './WebAuthnLoginButton';
import PasskeyOfferModal from './PasskeyOfferModal';
import { isPlatformAuthenticatorAvailable, shouldOfferPasskeySave } from '../../utils/webauthn';
import LoginVerification from './LoginVerification';
import LoginRejected from './LoginRejected';
import { pushRoute } from '../../navigation';

const TABS = [
  { id: 'phone', labelKey: 'auth.tab.phone' },
  { id: 'email', labelKey: 'auth.tab.email' },
  { id: 'username', labelKey: 'auth.tab.username' },
  { id: 'account', labelKey: 'auth.tab.account' },
];

const EMPTY_ERRORS = { login: '', password: '', form: '' };
const EXPIRED_ERRORS = new Set([
  'Doğrulama oturumu süresi doldu',
  'Doğrulama süresi doldu, tekrar giriş yapın',
]);

export default function LoginForm({ variant = 'page', onSuccess, onRegisterClick, onForgotClick, onPhaseChange }) {
  const { setSession } = useAuth();
  const _locale = useLocale();
  const tr = _locale.t;
  const validationCopy = buildAuthValidationCopy(tr);

  const [step, setStep] = useState('credentials');
  const [verifyStep, setVerifyStep] = useState('prompt');
  const [challenge, setChallenge] = useState(null);

  const [tab, setTab] = useState('phone');
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [loading, setLoading] = useState(false);
  const [authSettings, setAuthSettings] = useState(null);
  const [offerPasskey, setOfferPasskey] = useState(false);

  useEffect(() => {
    onPhaseChange?.(step);
  }, [step, onPhaseChange]);

  useEffect(() => {
    let cancelled = false;
    api.authSettings()
      .then((settings) => {
        if (!cancelled) setAuthSettings(settings);
      })
      .catch(() => {
        if (!cancelled) {
          setAuthSettings({
            socialProviders: {
              google: { enabled: false, url: '' },
              telegram: { enabled: false, url: '' },
              x: { enabled: false, url: '' },
            },
          });
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (step !== 'verify' || !challenge?.expiresAt) return undefined;

    const expiresAt = new Date(challenge.expiresAt).getTime();
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      setStep('expired');
      return undefined;
    }

    const timer = setTimeout(() => setStep('expired'), remaining);
    return () => clearTimeout(timer);
  }, [step, challenge?.expiresAt]);

  const clearFieldError = (field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const finishLogin = async (token, user, { fromPassword = false } = {}) => {
    if (!token || !user) return;
    setSession(token, user);
    if (fromPassword) {
      try {
        const platformOk = await isPlatformAuthenticatorAvailable();
        if (platformOk && shouldOfferPasskeySave()) {
          setOfferPasskey(true);
          onPhaseChange?.('passkey-offer');
          return;
        }
      } catch {}
    }
    onSuccess?.();
  };

  const resetVerification = () => {
    setStep('credentials');
    setVerifyStep('prompt');
    setChallenge(null);
    setErrors(EMPTY_ERRORS);
  };

  const markExpired = () => {
    setStep('expired');
    setErrors(EMPTY_ERRORS);
  };

  const isExpiredError = (message) => EXPIRED_ERRORS.has(message);

  const requestLogin = async () => {
    const value = tab === 'phone'
      ? normalizePhone(loginValue)
      : tab === 'account'
        ? String(loginValue).replace(/\D/g, '')
        : loginValue.trim();
    return api.login({
      method: tab,
      login: value,
      password,
      deviceId: getDeviceId(),
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const nextErrors = validateLogin({ tab, loginValue, password }, validationCopy);
    if (Object.keys(nextErrors).length) {
      setErrors({ ...EMPTY_ERRORS, ...nextErrors });
      return;
    }

    setErrors(EMPTY_ERRORS);
    setLoading(true);
    try {
      const res = await requestLogin();

      if (res.verificationRequired) {
        setChallenge(res.challenge);
        setStep('verify');
        setVerifyStep('prompt');
        return;
      }

      await finishLogin(res.token, res.user, { fromPassword: true });
    } catch (err) {
      const mapped = mapAuthApiError(err.message, validationCopy, validationCopy.loginInvalid);
      setErrors({ ...EMPTY_ERRORS, ...mapped });
    } finally {
      setLoading(false);
    }
  };

  const handleGetCode = async () => {
    setLoading(true);
    setErrors(EMPTY_ERRORS);
    try {
      const sent = await api.sendLoginCode({ challengeId: challenge.id });
      setVerifyStep('code');
      return sent;
    } catch (err) {
      if (isExpiredError(err.message)) {
        markExpired();
        throw err;
      }
      const mapped = mapAuthApiError(err.message, validationCopy, validationCopy.codeInvalid);
      setErrors({ ...EMPTY_ERRORS, ...mapped });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setErrors(EMPTY_ERRORS);
    try {
      return await api.sendLoginCode({ challengeId: challenge.id });
    } catch (err) {
      if (isExpiredError(err.message)) {
        markExpired();
        throw err;
      }
      const mapped = mapAuthApiError(err.message, validationCopy, validationCopy.codeInvalid);
      setErrors({ ...EMPTY_ERRORS, ...mapped });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (code) => {
    setLoading(true);
    setErrors(EMPTY_ERRORS);
    try {
      const res = await api.verifyLogin({
        challengeId: challenge.id,
        code,
        deviceId: getDeviceId(),
      });
      finishLogin(res.token, res.user);
    } catch (err) {
      if (isExpiredError(err.message)) {
        markExpired();
        return;
      }
      const mapped = mapAuthApiError(err.message, validationCopy, validationCopy.codeInvalid);
      setErrors({ ...EMPTY_ERRORS, ...mapped });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    setLoading(true);
    setErrors(EMPTY_ERRORS);
    try {
      const res = await requestLogin();
      if (res.verificationRequired) {
        setChallenge(res.challenge);
        setStep('verify');
        setVerifyStep('prompt');
        return;
      }
      finishLogin(res.token, res.user);
    } catch (err) {
      resetVerification();
      const mapped = mapAuthApiError(err.message, validationCopy, validationCopy.loginInvalid);
      setErrors({ ...EMPTY_ERRORS, ...mapped });
    } finally {
      setLoading(false);
    }
  };

  const onPhoneChange = (e) => {
    setLoginValue(formatPhoneInput(e.target.value));
    clearFieldError('login');
  };

  if (step === 'expired') {
    return (
      <LoginRejected
        onCancel={resetVerification}
        onRetry={handleRetry}
        loading={loading}
      />
    );
  }

  if (step === 'verify' && challenge) {
    return (
      <div className="auth-login__form">
        <LoginVerification
          challenge={challenge}
          step={verifyStep}
          onGetCode={handleGetCode}
          onCancel={resetVerification}
          onVerify={handleVerify}
          onResendCode={handleResendCode}
          loading={loading}
          error={errors.form}
        />
      </div>
    );
  }

  return (
    <form className="auth-login__form" onSubmit={submit} noValidate>
      <div className="auth-tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={`auth-tabs__item${tab === item.id ? ' is-active' : ''}`}
            onClick={() => {
              setTab(item.id);
              setLoginValue('');
              setErrors(EMPTY_ERRORS);
            }}
          >
            {tr(item.labelKey)}
            {tab === item.id && <span className="auth-tabs__indicator" />}
          </button>
        ))}
      </div>

      <div className="auth-field">
        {tab !== 'phone' && (
          <label className="auth-field__label">{tr(`auth.label.${tab}`)}</label>
        )}
        {tab === 'phone' ? (
          <>
            <label className="auth-field__label">{tr('auth.label.phone')}</label>
            <div className={`auth-field__control${errors.login ? ' auth-field__control--error' : ''}`}>
              <div className="auth-field__content">
                {!loginValue && (
                  <span className="auth-field__ghost">{phonePlaceholder()}</span>
                )}
                <input
                  type="tel"
                  className="auth-field__input"
                  name="phone"
                  value={loginValue}
                  onChange={onPhoneChange}
                  autoComplete="tel-country-code"
                />
              </div>
            </div>
            {errors.login && <div className="auth-field__error">{errors.login}</div>}
          </>
        ) : (
          <>
            <div className={`auth-field__control${errors.login ? ' auth-field__control--error' : ''}`}>
              <input
                type={tab === 'email' ? 'email' : 'text'}
                className="auth-field__input"
                value={loginValue}
                onChange={(e) => {
                  let next = e.target.value;
                  if (tab === 'username') {
                    next = next.replace(/\s+/g, '').toLowerCase();
                  } else if (tab === 'account') {
                    next = next.replace(/\D/g, '');
                  }
                  setLoginValue(next);
                  clearFieldError('login');
                }}
                placeholder={
                  tab === 'account'
                    ? tr('auth.placeholder.account')
                    : tab === 'email'
                      ? tr('auth.placeholder.email')
                      : tab === 'username'
                        ? tr('auth.placeholder.username')
                        : ''
                }
                autoComplete={tab === 'email' ? 'email' : tab === 'username' ? 'username' : 'off'}
                inputMode={tab === 'account' ? 'numeric' : undefined}
                pattern={tab === 'account' ? '[0-9]*' : undefined}
                spellCheck={tab === 'username' ? false : undefined}
                autoCapitalize={tab === 'username' ? 'none' : undefined}
              />
            </div>
            {errors.login && <div className="auth-field__error">{errors.login}</div>}
          </>
        )}
      </div>

      <div className="auth-field">
        <label className="auth-field__label">{tr('auth.label.password')}</label>
        <div className={`auth-field__control${errors.password ? ' auth-field__control--error' : ''}`}>
          <input
            type="password"
            className="auth-field__input"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearFieldError('password');
            }}
            autoComplete="current-password"
          />
        </div>
        {errors.password && <div className="auth-field__error">{errors.password}</div>}
      </div>

      {errors.form && <div className="auth-error">{errors.form}</div>}

      <a
        href="/authProcess/restorePassword"
        className="auth-forgot"
        onClick={(e) => {
          e.preventDefault();
          if (onForgotClick) onForgotClick();
          else pushRoute('restorePassword');
        }}
      >{tr('auth.forgot')}</a>

      <button type="submit" className="auth-submit" disabled={loading}>
        {loading ? '...' : tr('auth.login.submit')}
      </button>

      <WebAuthnLoginButton
        disabled={loading}
        onSuccess={(token, user) => finishLogin(token, user)}
      />

      <div className="auth__social auth__social--login">
        <AuthSocialProviders
          providers={authSettings?.socialProviders}
          titleKey="auth.login.orSignInWith"
        />
      </div>

      {variant === 'modal' && (
        <p className="auth-footer-link auth-footer-link--modal">
          {tr('auth.noAccount')}{' '}
          <a href="/authProcess/registration" onClick={(e) => { e.preventDefault(); onRegisterClick?.(); }}>
            {tr('auth.register.link')}
          </a>
        </p>
      )}

      <PasskeyOfferModal
        open={offerPasskey}
        onClose={() => {
          setOfferPasskey(false);
          onPhaseChange?.('credentials');
          onSuccess?.();
        }}
      />
    </form>
  );
}
